import { type InfiniteData, useInfiniteQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../contexts/analytics/hooks";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { useSnackbar } from "../contexts/SnackbarContext";
import { backendService } from "../services/backend.service";
import { getStaffingDate, getWeekStart } from "../utils/dateUtils";
import { camelCaseWeekShifts, withNurseDetails } from "../utils/departmentWeeks";
import type { Nurse, WeekData } from "../utils/models";
import { NURSE_SCHEDULE_QUERY_KEY } from "./useInvalidateNurseQueries";
import { useLastNameFirst } from "./useNameFormat";

// Every page is the same size, the first one included: a refetch rebuilds each page's range from the
// earliest one with getNextPageParam, so a first page of a different size would come back short.
const PAGE_WEEKS = 13;
// The first page starts this many weeks before the current one, so it shows recent history too.
const INITIAL_HISTORY_WEEKS = 5;
const SUNDAY = 0;
const DAYS_PER_WEEK = 7;
const PAGE_DAYS = PAGE_WEEKS * DAYS_PER_WEEK;
const ISO_DATE = "yyyy-MM-dd";

/** A Sunday-Saturday span, as ISO dates. */
type WeekRange = { startDate: string; endDate: string };

type NurseSchedulePage = {
	weeks: WeekData[];
	// The first and last day the department has a schedule for; no page is asked for past them. Both null
	// when it has no schedule yet.
	scheduleStartDate: string | null;
	scheduleEndDate: string | null;
};

const shiftDays = (isoDate: string, days: number): string => DateTime.fromISO(isoDate).plus({ days }).toFormat(ISO_DATE);

const sundayOf = (isoDate: string): string => getWeekStart(DateTime.fromISO(isoDate), SUNDAY).toFormat(ISO_DATE);

// Both ranges jump over weeks outside the schedule, so a page that came back empty because the schedule
// ended before it or starts after it still reaches the schedule's nearest weeks.
const laterRange = (range: WeekRange, schedule: NurseSchedulePage): WeekRange | undefined => {
	if (schedule.scheduleStartDate === null || schedule.scheduleEndDate === null || range.endDate >= schedule.scheduleEndDate) return undefined;
	const nextDay = shiftDays(range.endDate, 1);
	const scheduleFirstSunday = sundayOf(schedule.scheduleStartDate);
	const startDate = nextDay > scheduleFirstSunday ? nextDay : scheduleFirstSunday;
	return { startDate, endDate: shiftDays(startDate, PAGE_DAYS - 1) };
};

const earlierRange = (range: WeekRange, schedule: NurseSchedulePage): WeekRange | undefined => {
	if (schedule.scheduleStartDate === null || schedule.scheduleEndDate === null || range.startDate <= schedule.scheduleStartDate) return undefined;
	const previousDay = shiftDays(range.startDate, -1);
	const scheduleLastSaturday = shiftDays(sundayOf(schedule.scheduleEndDate), DAYS_PER_WEEK - 1);
	const endDate = previousDay < scheduleLastSaturday ? previousDay : scheduleLastSaturday;
	return { startDate: shiftDays(endDate, -(PAGE_DAYS - 1)), endDate };
};

/**
 * One nurse's weeks for the profile grid, loaded a range at a time: `department_data`'s week and day
 * slots holding only this nurse's scheduled and actual shifts. It starts around the current week and
 * grows in either direction on `loadEarlierWeeks` / `loadLaterWeeks`, stopping where the department's
 * schedule does — no week past the created schedule is ever asked for.
 */
export const useNurseScheduleWeeks = (nurse: Nurse) => {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { trackError } = useAnalytics();
	const { chosenDepartment } = useHospitalData();
	const departmentId = chosenDepartment.department_id;
	const lastNameFirst = useLastNameFirst();

	const initialRange = useMemo<WeekRange>(() => {
		const today = getStaffingDate(DateTime.now().setZone(chosenDepartment.timezone), chosenDepartment.dayStart);
		const currentSunday = getWeekStart(today, SUNDAY);
		return {
			startDate: currentSunday.minus({ weeks: INITIAL_HISTORY_WEEKS }).toFormat(ISO_DATE),
			endDate: currentSunday.minus({ weeks: INITIAL_HISTORY_WEEKS }).plus({ weeks: PAGE_WEEKS }).minus({ days: 1 }).toFormat(ISO_DATE),
		};
	}, [chosenDepartment.timezone, chosenDepartment.dayStart]);

	const selectWeeks = useCallback(
		(data: InfiniteData<NurseSchedulePage, WeekRange>) =>
			withNurseDetails(
				data.pages.flatMap((page) => page.weeks),
				nurse,
				lastNameFirst,
			),
		[nurse, lastNameFirst],
	);

	const query = useInfiniteQuery({
		// The first page's start is in the key: a cached schedule keeps its pages' ranges, so a revisit after the
		// week moved on (or the department's timezone or day start changed) would otherwise reuse the old window.
		queryKey: [...NURSE_SCHEDULE_QUERY_KEY, departmentId, nurse.id, initialRange.startDate],
		queryFn: async ({ pageParam }): Promise<NurseSchedulePage> => {
			const response = await backendService.getNurseSchedule(departmentId, nurse.id, pageParam.startDate, pageParam.endDate);
			return {
				weeks: camelCaseWeekShifts(response.data.weeks),
				scheduleStartDate: response.data.scheduleStartDate === null ? null : response.data.scheduleStartDate.toFormat(ISO_DATE),
				scheduleEndDate: response.data.scheduleEndDate === null ? null : response.data.scheduleEndDate.toFormat(ISO_DATE),
			};
		},
		initialPageParam: initialRange,
		getNextPageParam: (lastPage, _allPages, lastPageParam) => laterRange(lastPageParam, lastPage),
		getPreviousPageParam: (firstPage, _allPages, firstPageParam) => earlierRange(firstPageParam, firstPage),
		select: selectWeeks,
		// The profile holds an empty placeholder nurse (id 0) until its own query hydrates it.
		enabled: nurse.id > 0,
	});

	useEffect(() => {
		if (query.isError) {
			trackError("nurse_schedule_fetch_error", { department_id: departmentId, nurse_id: nurse.id });
			showSnackbar(t("error.nurse.schedule.data"), "error");
		}
	}, [query.isError, departmentId, nurse.id, trackError, showSnackbar, t]);

	const {
		hasPreviousPage,
		hasNextPage,
		isFetchingPreviousPage,
		isFetchingNextPage,
		isFetchPreviousPageError,
		isFetchNextPageError,
		fetchPreviousPage,
		fetchNextPage,
	} = query;

	// A failed page isn't retried on the next scroll: the error was already shown once, and re-asking on
	// every scroll event would repeat it. A refresh or the next shift write starts over.
	const loadEarlierWeeks = useCallback(() => {
		if (hasPreviousPage && !isFetchingPreviousPage && !isFetchPreviousPageError) void fetchPreviousPage();
	}, [hasPreviousPage, isFetchingPreviousPage, isFetchPreviousPageError, fetchPreviousPage]);

	const loadLaterWeeks = useCallback(() => {
		if (hasNextPage && !isFetchingNextPage && !isFetchNextPageError) void fetchNextPage();
	}, [hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage]);

	// The grid asks for more weeks only as rows scroll into view, so with no rows nothing would ever ask.
	// When the loaded pages hold no weeks but the schedule lies further on, this keeps fetching toward it.
	const isReachingSchedule = query.data?.length === 0 && (hasNextPage || hasPreviousPage) && !isFetchNextPageError && !isFetchPreviousPageError;
	useEffect(() => {
		if (!isReachingSchedule) return;
		if (hasNextPage) loadLaterWeeks();
		else loadEarlierWeeks();
	}, [isReachingSchedule, hasNextPage, loadLaterWeeks, loadEarlierWeeks]);

	return {
		weeks: query.data,
		// `isPending`, not `isLoading`: it stays true while the query waits for a real nurse id.
		isLoading: query.isPending || isReachingSchedule,
		isError: query.isError,
		isLoadingMoreWeeks: isFetchingPreviousPage || isFetchingNextPage,
		loadEarlierWeeks,
		loadLaterWeeks,
	};
};
